import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { I18N_KEYS } from '@/language/keys';
import { getAxiosInstance } from '@/services/axiosConfig';
import useWorkspacesStore from '@/state/workspaces.state';
import { useGlobalStore } from '@/state/global.state';
import { log } from '@/logger/logger.ts';
import { CreditThresholdRule, CreditThresholdRuleAction, CreditThresholdRuleType } from '@/types/auth.types';
import { CreditsContext } from '@/services/CreditsContext';
import { useSubscriptionCredits } from './useSubscriptionCredits';

const logger = log.getLogger('useCreditThresholdAlerts');
const axiosInstance = getAxiosInstance();
const CREDIT_THRESHOLD_PERCENTAGE = 0.2;
const DEBOUNCE_MS = 500;

export const useCreditThresholdAlerts = () => {
  const { t } = useTranslation();
  const { workspaceCredits } = useContext(CreditsContext);
  const { isEnterpriseSubscription, monthlyCreditsAllocation } = useSubscriptionCredits();
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const activeWorkspacePreferences = useWorkspacesStore((state) => state.activeWorkspace.preferences);
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const { updateSnackbarData } = useGlobalStore();

  const existingRule = activeWorkspacePreferences?.creditThresholdRules?.[0] ?? null;
  const enabled = existingRule?.enabled ?? false;

  const defaultThreshold = Math.round(
    (isEnterpriseSubscription ? (workspaceCredits ?? 0) : (monthlyCreditsAllocation ?? 0)) *
      CREDIT_THRESHOLD_PERCENTAGE,
  );

  const threshold = existingRule?.threshold ?? defaultThreshold;

  const thresholdInputRef = useRef<HTMLInputElement>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const [thresholdInputValue, setThresholdInputValue] = useState(threshold);

  // Refs for debounce: timer and the pre-window state used for rollback on error.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preDebounceRulesRef = useRef(activeWorkspacePreferences?.creditThresholdRules);

  const { mutate: fireApiCall } = useMutation({
    mutationFn: (rules: CreditThresholdRule[]) =>
      axiosInstance.put(`/v1/workspaces/${workspaceId}`, { creditThresholdRules: rules }),
    onMutate: () => ({ previousRules: preDebounceRulesRef.current }),
    onError: (_err, _rules, context) => {
      logger.error('Error updating credit threshold rules', _err);
      updateActiveWorkspace({
        preferences: { ...activeWorkspacePreferences, creditThresholdRules: context?.previousRules },
      });
      updateSnackbarData({
        text: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CREDIT_THRESHOLD_ALERTS_ERROR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });

  const persistRules = (rules: CreditThresholdRule[]) => {
    // Update store immediately so the UI reflects the change right away.
    updateActiveWorkspace({ preferences: { ...activeWorkspacePreferences, creditThresholdRules: rules } });

    if (!debounceTimerRef.current) {
      // Start of a new debounce window — capture state before any changes for rollback.
      preDebounceRulesRef.current = activeWorkspacePreferences?.creditThresholdRules;
    }

    clearTimeout(debounceTimerRef.current!);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      fireApiCall(rules);
    }, DEBOUNCE_MS);
  };

  const handleCreditThresholdToggle = (nextEnabled: boolean) => {
    if (nextEnabled) {
      setThresholdInputValue(threshold);
      const newRule = {
        id: existingRule?.id,
        threshold,
        thresholdType: CreditThresholdRuleType.Absolute,
        action: CreditThresholdRuleAction.Notify,
        enabled: true,
        triggeredAt: existingRule?.triggeredAt,
      };
      persistRules([newRule]);
      // Defer focus until after React re-renders and mounts the input
      setTimeout(() => thresholdInputRef.current?.focus(), 0);
    } else {
      // Set enabledRef before blur so handleCreditThresholdChange (fired by onBlur)
      // sees enabled=false and skips the conflicting save.
      persistRules([{ ...existingRule!, enabled: false }]);
      enabledRef.current = false;
      thresholdInputRef.current?.blur();
    }
  };

  const handleThresholdInputChange = (value: number) => {
    setThresholdInputValue(value);
  };

  const handleCreditThresholdCancel = () => {
    if (!existingRule) return;
    setThresholdInputValue(existingRule.threshold);
  };

  const handleCreditThresholdChange = (value: number) => {
    if (!enabledRef.current || !existingRule) return;
    const resolvedThreshold = isNaN(value) || value < 0 ? threshold : value;
    setThresholdInputValue(resolvedThreshold);
    persistRules([{ ...existingRule, threshold: resolvedThreshold }]);
  };

  return {
    enabled,
    thresholdInputValue,
    thresholdInputRef,
    handleCreditThresholdToggle,
    handleThresholdInputChange,
    handleCreditThresholdChange,
    handleCreditThresholdCancel,
  };
};
